import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Form, Input, Button, Alert, Typography, Card, Flex, theme } from "antd";
import { supabase } from "@/configs/supabase/config";

export const Route = createFileRoute("/_auth/update-password")({
  beforeLoad: async () => {
    const sb_Auth_GetSession = await supabase.auth.getSession();
    if (!sb_Auth_GetSession.data.session) {
      throw redirect({ to: "/login" });
    }
  },
  component: Page_UpdatePassword,
});

function Page_UpdatePassword() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { password: string }) => {
    setError(null);
    setLoading(true);

    const sb_Auth_UpdateUser = await supabase.auth.updateUser({
      password: values.password,
    });

    setLoading(false);

    if (sb_Auth_UpdateUser.error) {
      setError(sb_Auth_UpdateUser.error.message);
      return;
    }

    await supabase.auth.signOut();
    navigate({ to: "/login", search: { message: "password-updated" } as never });
  };

  return (
    <Card style={{ width: 400 }} styles={{ body: { padding: token.paddingXL } }}>
      <Flex vertical align="center" gap={token.marginMD}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Set New Password
        </Typography.Title>
      </Flex>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginTop: token.marginMD }}
        />
      )}

      <Form layout="vertical" onFinish={handleSubmit} style={{ marginTop: token.marginLG }}>
        <Form.Item
          name="password"
          label="New Password"
          rules={[
            { required: true, message: "Password is required" },
            { min: 6, message: "Minimum 6 characters" },
          ]}
        >
          <Input.Password size="large" placeholder="New password" autoFocus />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue("password") === value
                  ? Promise.resolve()
                  : Promise.reject(new Error("Passwords do not match")),
            }),
          ]}
        >
          <Input.Password size="large" placeholder="Confirm password" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={loading}>
            Update password
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
